import { useQueryClient } from "@tanstack/react-query";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { usePusherEvent } from "PFCore/base/pusher";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { activityKeys } from "PFCore/hooks/queries/activity/query_keys";
import { useActivityBulkFillAndBook } from "PFCore/hooks/queries/activity/use_activity_bulk_fill_and_book";
import { queryKeys as vacanciesKeys } from "PFCore/hooks/queries/activity/vacancies/query_keys";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { profileKeys } from "PFCore/hooks/queries/profile/query_keys";
import { useInvalidateManageViewRoles } from "PFCore/hooks/queries/roles/use_invalidate_manage_view_roles";
import { useShortlistsInvalidate } from "PFCore/hooks/queries/shortlists/use_shortlists_invalidate";
import {
  BULK_OPERATIONS_TERMINAL_STATES,
  BulkOperationsPusherData,
  BulkOperationsState
} from "PFCore/services/bulk_operations";
import { Id } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type PendingOperation = {
  id: Id;
  activityIds: Id[];
};

export const useManageRolesBulkFillAndBook = () => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });
  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();
  const { invalidateAll } = useInvalidateManageViewRoles();
  const { invalidateShortlists, invalidateRankedShortlists } = useShortlistsInvalidate();
  const { data: currentProfile } = useCurrentProfile();
  const queryClient = useQueryClient();

  const [pending, setPending] = useState<PendingOperation | undefined>(undefined);

  const mutation = useActivityBulkFillAndBook({
    onSuccess: ({ id }, { activityIds }) => {
      setPending({ id, activityIds });
      showGrowl({ message: t("fillAndBookStarted") });
    },
    onError: (error) => {
      showGrowl({ message: getFirstError(error) || t("fillAndBookError"), kind: "error" });
    }
  });

  usePusherEvent(
    `private-profile-${currentProfile.id}`,
    "bulk_operation_completed",
    (data: BulkOperationsPusherData) => {
      if (!pending || data.id !== pending.id) {
        return;
      }
      if (!BULK_OPERATIONS_TERMINAL_STATES.includes(data.state)) {
        return;
      }

      invalidateAll();
      pending.activityIds.forEach((activityId) => {
        invalidateShortlists(activityId);
        invalidateRankedShortlists(activityId);
        queryClient.invalidateQueries({ queryKey: vacanciesKeys.vacancies(activityId) });
        queryClient.invalidateQueries({ queryKey: activityKeys.activity(activityId) });
      });
      queryClient.invalidateQueries({ queryKey: profileKeys.all() });

      if (data.state === BulkOperationsState.Completed) {
        showGrowl({ message: t("fillAndBookCompleted") });
      } else if (data.state === BulkOperationsState.PartiallyCompleted) {
        showGrowl({ message: t("fillAndBookPartial"), kind: "alert" });
      } else {
        showGrowl({ message: t("fillAndBookError"), kind: "error" });
      }

      setPending(undefined);
    },
    !!pending
  );

  return {
    mutate: mutation.mutate,
    isProcessing: mutation.isPending || !!pending
  };
};
